import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';
import Button from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message?: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  confirmText = 'OK',
  cancelText = 'Annuler',
  onConfirm,
  onCancel,
  showCancel = false,
  size = 'md',
  children
}) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-eg-valide" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-eg-accent" />;
      default:
        return <Info className="w-6 h-6 text-eg-accent" />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return 'border-eg-valide/30';
      case 'warning':
        return 'border-yellow-200';
      case 'error':
        return 'border-eg-accent/30';
      default:
        return 'border-blue-200';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-2xl';
      case 'xl':
        return 'max-w-4xl';
      default:
        return 'max-w-md';
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              className={`bg-eg-blanc rounded-md shadow-xl ${getSizeClasses()} w-full border-2 ${getBorderColor()} my-8 relative z-[101]`}
            >
            <div className={size === 'md' ? 'p-4' : 'p-6'}>
              <div className={`flex items-center justify-between ${size === 'md' ? 'mb-3' : 'mb-4'}`}>
                <div className="flex items-center space-x-3">
                  {!children && getIcon()}
                  <h3 className={`${size === 'md' ? 'text-base' : 'text-lg'} font-semibold text-eg-encre`}>{title}</h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-eg-estompe hover:text-eg-gris transition-colors"
                >
                  <X className={size === 'md' ? 'w-4 h-4' : 'w-5 h-5'} />
                </button>
              </div>

              {children ? (
                <div className={size === 'md' ? 'mb-0' : 'mb-6'}>
                  {children}
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <p className="text-eg-brun whitespace-pre-line">{message}</p>
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    {showCancel && (
                      <Button variant="outline" onClick={handleCancel}>
                        {cancelText}
                      </Button>
                    )}
                    <Button 
                      variant={type === 'error' ? 'outline' : 'primary'} 
                      onClick={handleConfirm}
                      className={type === 'error' ? 'text-eg-accent border-red-300 hover:bg-eg-accent/10' : ''}
                    >
                      {confirmText}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      )}
    </AnimatePresence>
  );
};

export default Modal;